import { getters } from './getters';
import { actions } from './actions';
import { mutations } from './mutations';

/**
 * @typedef {object} BrowserInfo
 * @property {string} device_name
 * @property {string} browser_name
 * @property {string} platform_name
 * @property {string} browser_version
 * @property {string} platform_version
 */

/**
 * @typedef {object} InitiatedAt
 * @property {string} timestamp
 */

/**
 * @typedef {object} LastNonActivityMessageSender
 * @property {object} additional_attributes
 * @property {string} additional_attributes.created_at_ip
 * @property {object} custom_attributes
 * @property {string} email
 * @property {number} id
 * @property {null | string} identifier
 * @property {string} name
 * @property {null | string} phone_number
 * @property {string} thumbnail
 * @property {boolean} blocked
 * @property {string} type
 */

/**
 * @typedef {object} LastNonActivityMessageConversation
 * @property {number} assignee_id
 * @property {number} unread_count
 * @property {number} last_activity_at Unix timestamp
 * @property {object} contact_inbox
 * @property {string} contact_inbox.source_id
 */

/**
 * @typedef {object} MessageItem
 * @property {number} id
 * @property {string} content
 * @property {number} account_id
 * @property {number} inbox_id
 * @property {number} conversation_id
 * @property {number} message_type
 * @property {number} created_at Unix timestamp
 * @property {string} updated_at ISO timestamp
 * @property {boolean} private
 * @property {string} status
 * @property {null | string} source_id
 * @property {string} content_type
 * @property {object} content_attributes
 * @property {null | any} content_attributes.in_reply_to - Adjust type if known
 * @property {string} sender_type
 * @property {number} sender_id
 * @property {object} external_source_ids
 * @property {object} additional_attributes
 * @property {string} processed_message_content
 * @property {object} sentiment
 * @property {LastNonActivityMessageConversation} conversation
 * @property {LastNonActivityMessageSender} sender
 */

/**
 * @typedef {object} MetaSender
 * @property {object} additional_attributes
 * @property {string} additional_attributes.created_at_ip
 * @property {string} availability_status
 * @property {string} email
 * @property {number} id
 * @property {string} name
 * @property {null | string} phone_number
 * @property {boolean} blocked
 * @property {null | string} identifier
 * @property {string} thumbnail
 * @property {object} custom_attributes
 * @property {number} last_activity_at Unix timestamp
 * @property {number} created_at Unix timestamp
 */

/**
 * @typedef {object} MetaAssignee
 * @property {number} id
 * @property {number} account_id
 * @property {null | string} availability_status
 * @property {boolean} auto_offline
 * @property {boolean} confirmed
 * @property {string} email
 * @property {string} available_name
 * @property {string} name
 * @property {string} role
 * @property {string} thumbnail
 * @property {null | number} custom_role_id
 */

/**
 * @typedef {object} ConversationMeta
 * @property {MetaSender} sender
 * @property {MetaAssignee | null} assignee - Can be null if unassigned
 * @property {string} channel
 * @property {boolean} hmac_verified
 * @property {number} unread_count
 * @property {string} contact_last_seen_at
 */

/**
 * @typedef {object} ConversationAdditionalAttributes
 * @property {BrowserInfo} browser
 * @property {null | string} referer
 * @property {InitiatedAt} initiated_at
 * @property {string} browser_language
 * @property {number} contact_inbox_id
 * @property {string} uuid
 * @property {null | string} identifier
 * @property {string} last_activity_at ISO timestamp
 * @property {null | number} team_id
 * @property {null | number} campaign_id
 * @property {null | string} snoozed_until ISO timestamp
 * @property {object} custom_attributes
 * @property {string} assignee_last_seen_at ISO timestamp
 * @property {string} contact_last_seen_at ISO timestamp
 * @property {string} first_reply_created_at ISO timestamp
 * @property {null | string} priority
 * @property {null | number} sla_policy_id
 * @property {string} waiting_since ISO timestamp
 * @property {string} cached_label_list Comma-separated string
 * @property {null | string} provider_conversation_id
 * @property {string[]} label_list
 */

/**
 * @typedef {object} UiFlags
 * @property {boolean} allMessagesLoaded
 * @property {boolean} isFetchingList
 * @property {boolean} isAgentTyping
 * @property {boolean} isCreating
 */

/**
 * Represents a conversation item in the list.
 * @typedef {object} ConversationListItem
 * @property {number} id
 * @property {number} account_id
 * @property {number} inbox_id
 * @property {string} status
 * @property {number} assignee_id
 * @property {string} created_at ISO timestamp
 * @property {string} updated_at ISO timestamp
 * @property {number} contact_id
 * @property {number} display_id
 * @property {Record<string, any>} messages
 * @property {ConversationAdditionalAttributes} additional_attributes
 * @property {ConversationMeta} meta
 * @property {UiFlags} uiFlags
 * @property {string} lastMessageId
 * @property {MessageItem | null} last_non_activity_message
 */

const state = {
  currentConversationId: null,
  /** @type {Record<string, ConversationListItem>} */
  details: {},
  isFetchingList: false,
  isCreating: false,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
